import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { Copy, ExternalLink, Loader2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@artstorefronts/ui';
import AdminLayout from '../../../components/AdminLayout';
import {
  ADMIN_CREATE_QUEST,
  ADMIN_QUESTS,
  ADMIN_SET_QUEST_STATUS,
  STATUS_PILL_CLASSES,
  audienceSummary,
  questLandingUrl,
  questOptInUrl,
  questsIndexUrl,
  stepHarnessUrl,
  type AdminQuest,
} from './quest-shared';
import { UserQuestLookup } from './UserQuestLookup';

/**
 * The Quests admin list — the pre-feeds list page shape: AdminLayout, a CSS-grid table,
 * a minimal name-only create dialog, status changes behind consequence-stating dialogs.
 *
 * The Share column is the point of the page: every quest has ONE opt-in URL that can be
 * mass-emailed, and this is where an admin copies it from.
 */
export default function AdminQuests() {
  const navigate = useNavigate();
  const { data, loading, refetch } = useQuery<{ adminQuests: AdminQuest[] }>(ADMIN_QUESTS, {
    fetchPolicy: 'cache-and-network',
  });
  const [createQuest, { loading: creating }] = useMutation(ADMIN_CREATE_QUEST);
  const [setStatus] = useMutation(ADMIN_SET_QUEST_STATUS);

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [statusTarget, setStatusTarget] = useState<{ quest: AdminQuest; status: AdminQuest['status'] } | null>(null);

  const quests = data?.adminQuests ?? [];

  const handleCreate = async () => {
    if (!newName.trim()) return;
    try {
      const result = await createQuest({ variables: { input: { name: newName.trim() } } });
      setCreateOpen(false);
      setNewName('');
      navigate(`/admin/quests/${result.data.adminCreateQuest.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create quest');
    }
  };

  const handleStatus = async () => {
    if (!statusTarget) return;
    try {
      await setStatus({ variables: { id: statusTarget.quest.id, status: statusTarget.status } });
      toast.success(`"${statusTarget.quest.name}" is now ${statusTarget.status.toLowerCase()}.`);
      setStatusTarget(null);
      void refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to change status');
    }
  };

  const copyOptIn = async (quest: AdminQuest) => {
    await navigator.clipboard.writeText(questOptInUrl(quest.slug));
    toast.success('Opt-in link copied — paste it into an email or a post.');
  };

  return (
    <AdminLayout
      title="Quests"
      description="Named checklists of measurable steps. Auto-assigned by intent, or joined via each quest's opt-in link."
      action={
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild>
            <a href={questsIndexUrl()} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="mr-2 h-4 w-4" /> Member quests page
            </a>
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New quest
          </Button>
        </div>
      }
    >
      <p className="mb-4 text-sm text-gray-600">
        {/* The behaviours a step can open — the pickers, the Arty panel, the artwork modal
            — are all town-square components, so the harness lives there and this links
            out to it, exactly as the editor's Preview button already does. */}
        <a
          className="font-medium text-indigo-600 hover:underline"
          href={stepHarnessUrl()}
          target="_blank"
          rel="noopener noreferrer"
        >
          Preview all steps →
        </a>{' '}
        <span className="text-gray-500">every catalog step on one page, clickable, without completing anything.</span>
      </p>
      <UserQuestLookup />
      {loading && quests.length === 0 ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : quests.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">
          No quests yet. Create one, add steps from the catalog, and publish it.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-[1fr_80px_1fr_90px_70px_60px_120px_170px] items-center gap-2 border-b px-2 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <span>Name</span>
              <span>Status</span>
              <span>Audience</span>
              <span>Size</span>
              <span>Priority</span>
              <span>Steps</span>
              <span>Share</span>
              <span>Actions</span>
            </div>
            {quests.map((quest) => (
              <div
                key={quest.id}
                className="grid grid-cols-[1fr_80px_1fr_90px_70px_60px_120px_170px] items-center gap-2 border-b px-2 py-3 text-sm"
              >
                <span className="truncate">
                  <Link to={`/admin/quests/${quest.id}`} className="font-medium text-blue-600 hover:underline">
                    {quest.name}
                  </Link>
                  <span className="ml-2 text-xs text-gray-400">/{quest.slug}</span>
                </span>
                <span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_PILL_CLASSES[quest.status]}`}>
                    {quest.status}
                  </span>
                </span>
                <span
                  className="truncate text-gray-600"
                  title={quest.assignment === 'optin' ? 'Opt-in only' : audienceSummary(quest)}
                >
                  {quest.assignment === 'optin' ? (
                    <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                      Opt-in only
                    </span>
                  ) : (
                    audienceSummary(quest)
                  )}
                  {!quest.directoryVisible ? (
                    <span className="ml-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                      Hidden
                    </span>
                  ) : null}
                </span>
                {quest.assignment === 'optin' ? (
                  <span className="text-gray-400" title="Opt-in quests have no audience criteria">
                    —
                  </span>
                ) : (
                  <span
                    className="text-gray-600"
                    title={`${quest.audienceCount.toLocaleString()} of ${quest.audienceTotal.toLocaleString()} current users match this audience`}
                  >
                    {quest.audienceCount.toLocaleString()}
                    <span className="text-xs text-gray-400"> / {quest.audienceTotal.toLocaleString()}</span>
                  </span>
                )}
                <span className="text-gray-600">{quest.priority}</span>
                <span className="text-gray-600">{quest.steps.length}</span>
                <span>
                  <Button variant="outline" size="sm" onClick={() => void copyOptIn(quest)}>
                    <Copy className="mr-1 h-3 w-3" /> Opt-in link
                  </Button>
                </span>
                <span className="flex gap-1">
                  <Button variant="outline" size="sm" asChild>
                    <a
                      href={questLandingUrl(quest.slug)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={
                        quest.status === 'LIVE'
                          ? 'Open the live quest page'
                          : 'Preview the quest page (admins only until live)'
                      }
                    >
                      <ExternalLink className="mr-1 h-3 w-3" /> {quest.status === 'LIVE' ? 'View' : 'Preview'}
                    </a>
                  </Button>
                  {quest.status !== 'LIVE' ? (
                    <Button size="sm" onClick={() => setStatusTarget({ quest, status: 'LIVE' })}>
                      Go live
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" onClick={() => setStatusTarget({ quest, status: 'DRAFT' })}>
                      To draft
                    </Button>
                  )}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New quest</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="quest-name">Name</Label>
            <Input
              id="quest-name"
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="e.g. Selling more art"
              onKeyDown={(event) => {
                if (event.key === 'Enter') void handleCreate();
              }}
            />
            <p className="text-xs text-gray-500">Everything else — steps, audience, copy — is set in the editor.</p>
          </div>
          <Button onClick={() => void handleCreate()} disabled={creating || !newName.trim()}>
            {creating ? 'Creating…' : 'Create and open editor'}
          </Button>
        </DialogContent>
      </Dialog>

      <AlertDialog open={statusTarget !== null} onOpenChange={(open) => !open && setStatusTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {statusTarget?.status === 'LIVE' ? 'Publish this quest?' : 'Take this quest to draft?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {statusTarget?.status === 'LIVE'
                ? 'Members matching its audience will start seeing it immediately, and its opt-in link goes live.'
                : 'Members currently on it will stop seeing it, and its opt-in link stops working.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleStatus()}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
